import React, { Component, ErrorInfo } from "react";
import { Button, Result, Alert } from "antd";
import { IconRefresh, IconHome } from "@tabler/icons-react";
import { config } from "../../../../config";
import Logger from "../../../utils/logger";
import { captureException, addBreadcrumb } from "../../../utils/sentry";

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Logger.error("Error Boundary Caught Error:", error);
    Logger.error("Error Info:", errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Add breadcrumb for context
    addBreadcrumb({
      category: "error-boundary",
      message: `Error caught in ErrorBoundary: ${error.message}`,
      level: "error",
      data: {
        componentStack: errorInfo.componentStack,
      },
    });

    // Capture error in Sentry
    captureException(error, {
      tags: {
        "error.boundary": "true",
        "error.type": this.isNetworkError(error) ? "network" : "runtime",
      },
      extra: {
        componentStack: errorInfo.componentStack,
      },
      level: "error",
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  // Check if error is network-related
  isNetworkError = (error?: Error): boolean => {
    if (!error) return false;
    const networkErrorPatterns = [
      "Failed to fetch",
      "NetworkError",
      "fetch dynamically imported module",
      "Loading chunk",
      "Loading CSS chunk",
      "TypeError: NetworkError",
      "ERR_INTERNET_DISCONNECTED",
      "ERR_NETWORK_CHANGED",
    ];
    return networkErrorPatterns.some((pattern) =>
      error.message?.toLowerCase().includes(pattern.toLowerCase())
    );
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      const isNetworkIssue = this.isNetworkError(error);

      // Use custom fallback if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent error={error} resetError={this.handleReset} />
        );
      }

      // Network connectivity error - user-friendly display for both dev and prod
      if (isNetworkIssue) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
            <div className="max-w-md w-full text-center">
              <div className="mb-6">
                <div className="w-20 h-20 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-orange-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                  Connection Issue
                </h1>
                <p className="text-gray-600 mb-6">
                  It looks like you're experiencing connectivity issues. Please
                  check your internet connection and try again.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  type="primary"
                  size="large"
                  icon={<IconRefresh size={16} />}
                  onClick={() => window.location.reload()}
                  className="w-full"
                >
                  Refresh Page
                </Button>
                <Button
                  size="large"
                  icon={<IconHome size={16} />}
                  onClick={this.handleGoHome}
                  className="w-full"
                >
                  Go to Home
                </Button>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="text-sm font-medium text-blue-900 mb-2">
                  Troubleshooting Tips:
                </h3>
                <ul className="text-sm text-blue-800 text-left space-y-1">
                  <li>• Check your internet connection</li>
                  <li>• Try refreshing the page</li>
                  <li>• Disable any VPN or proxy</li>
                  <li>• Clear browser cache and cookies</li>
                </ul>
              </div>

              {config.isDevelopment() && (
                <Alert
                  message="Developer Info"
                  description={error?.message}
                  type="info"
                  showIcon
                  className="mt-4 text-left"
                />
              )}
            </div>
          </div>
        );
      }

      // Development error display - detailed information
      if (config.isDevelopment()) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4 py-8">
            <div className="max-w-4xl w-full">
              <Result
                status="error"
                title="Development Error"
                subTitle="An error occurred while rendering this component. Check the details below."
                extra={[
                  <Button
                    key="retry"
                    type="primary"
                    size="large"
                    icon={<IconRefresh size={18} />}
                    onClick={this.handleReset}
                  >
                    Try Again
                  </Button>,
                  <Button
                    key="home"
                    size="large"
                    icon={<IconHome size={18} />}
                    onClick={this.handleGoHome}
                  >
                    Go Home
                  </Button>,
                ]}
              />

              {/* Error Details */}
              <div className="mt-8 space-y-4 bg-white rounded-xl p-6 shadow-lg">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Error Message:
                  </h3>
                  <pre className="p-4 bg-red-50 rounded-lg border border-red-200 text-red-700 text-sm overflow-auto">
                    {error?.message || "Unknown error"}
                  </pre>
                </div>

                <details className="group">
                  <summary className="cursor-pointer text-sm font-semibold text-gray-700 hover:text-gray-900 flex items-center gap-2">
                    <span className="transform transition-transform group-open:rotate-90">
                      ▶
                    </span>
                    Stack Trace
                  </summary>
                  <pre className="mt-2 p-4 bg-slate-50 rounded-lg border border-slate-200 text-slate-700 text-xs overflow-auto max-h-64">
                    {error?.stack || "No stack trace available"}
                  </pre>
                </details>

                {errorInfo && (
                  <details className="group">
                    <summary className="cursor-pointer text-sm font-semibold text-gray-700 hover:text-gray-900 flex items-center gap-2">
                      <span className="transform transition-transform group-open:rotate-90">
                        ▶
                      </span>
                      Component Stack
                    </summary>
                    <pre className="mt-2 p-4 bg-blue-50 rounded-lg border border-blue-200 text-blue-700 text-xs overflow-auto max-h-64">
                      {errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        );
      }

      // Production error display - user-friendly, clean, minimal
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4">
          <Result
            status="500"
            title="Oops! Something went wrong"
            subTitle="We're sorry for the inconvenience. Our team has been notified and is working on a fix."
            extra={[
              <Button
                key="retry"
                type="primary"
                size="large"
                icon={<IconRefresh size={18} />}
                onClick={this.handleReset}
              >
                Try Again
              </Button>,
              <Button
                key="home"
                size="large"
                icon={<IconHome size={18} />}
                onClick={this.handleGoHome}
              >
                Back Home
              </Button>,
            ]}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-based error boundary for functional components
 */
export const useErrorHandler = () => {
  const handleError = (error: Error, errorInfo?: ErrorInfo) => {
    Logger.error("Error Handler:", error);
    if (errorInfo) {
      Logger.error("Error Info:", errorInfo);
    }

    // Throw error to be caught by nearest error boundary
    throw error;
  };

  return handleError;
};

/**
 * Higher-order component for wrapping components with error boundary
 */
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${
    Component.displayName || Component.name
  })`;
  return WrappedComponent;
};

export default ErrorBoundary;
